import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, daysUntil } from "../../lib/api";
import { CheckCircle2, ListTodo, Users, DollarSign, Sparkles, Building2, ArrowUpRight, Phone, Globe } from "lucide-react";
import { Button } from "../ui/button";
import ApplyTemplateDialog from "../ApplyTemplateDialog";
import WeatherWidget from "../WeatherWidget";

export default function OverviewTab({ ev, members, canEdit = true }) {
  const [stats, setStats] = useState({ tasks: 0, tasksDone: 0, guests: 0, rsvpYes: 0, budgetPlanned: 0, budgetActual: 0, vendorCount: 0 });
  const [linkedVenue, setLinkedVenue] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, g, b] = await Promise.all([
          api.get(`/events/${ev.id}/tasks`),
          api.get(`/events/${ev.id}/guests`),
          api.get(`/events/${ev.id}/budget`),
        ]);
        setStats({
          tasks: t.data.length,
          tasksDone: t.data.filter(x => x.status === "done").length,
          guests: g.data.reduce((s, x) => s + (x.party_size || 1), 0),
          rsvpYes: g.data.filter(x => x.rsvp_status === "yes").reduce((s, x) => s + (x.party_size || 1), 0),
          budgetPlanned: b.data.reduce((s, x) => s + (x.planned || 0), 0),
          budgetActual: b.data.reduce((s, x) => s + (x.actual || 0), 0),
          vendorCount: members.filter(m => m.role === "vendor").length,
        });
      } catch (e) { console.error("Overview stats load failed:", e); }
    })();
  }, [ev.id, members]);

  useEffect(() => {
    if (!ev.venue_id) { setLinkedVenue(null); return; }
    (async () => {
      try { const { data } = await api.get(`/venues/${ev.venue_id}`); setLinkedVenue(data); }
      catch { setLinkedVenue(null); }
    })();
  }, [ev.venue_id]);

  const days = daysUntil(ev.event_date);

  return (
    <div className="space-y-10">
      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={ListTodo} label="Tasks" primary={`${stats.tasksDone}/${stats.tasks}`} sub="completed" />
        <Stat icon={Users} label="Guests confirmed" primary={`${stats.rsvpYes}`} sub={`of ${stats.guests} invited`} />
        <Stat icon={DollarSign} label="Budget" primary={money(stats.budgetActual)} sub={`of ${money(stats.budgetPlanned)} planned`} />
        <Stat icon={CheckCircle2} label="Vendors" primary={`${stats.vendorCount}`} sub="on the team" />
      </div>

      {/* Details */}
      <section className="bevel border-hair rounded-sm p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="eyebrow">The story</div>
          {stats.tasks === 0 && (
            <Button
              onClick={() => setTplOpen(true)}
              data-testid="overview-apply-template-btn"
              variant="outline"
              className="rounded-sm border-[#0A0A0A] text-app hover:bg-surface-2 h-9"
            >
              <Sparkles size={14} strokeWidth={1.5} className="mr-2" /> Apply template
            </Button>
          )}
        </div>
        {ev.description ? (
          <p className="font-serif text-2xl text-app leading-snug max-w-3xl">{ev.description}</p>
        ) : (
          <p className="text-muted font-light">No description yet. Add one from the event settings to give your team the vision.</p>
        )}

        <div className="mt-8 pt-6 border-t border-hair grid grid-cols-2 md:grid-cols-4 gap-6">
          <Detail label="Countdown" value={days !== null && days >= 0 ? (days === 0 ? "Today" : `${days}d`) : "—"} />
          <Detail label="Date" value={new Date(ev.event_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric"})} />
          <Detail label="Venue" value={ev.venue || "—"} />
          <Detail label="Host(s)" value={ev.couple_names || "—"} />
        </div>
      </section>

      <ApplyTemplateDialog
        eventId={ev.id}
        eventType={ev.event_type}
        open={tplOpen}
        onOpenChange={setTplOpen}
        onApplied={() => window.location.reload()}
      />

      {/* Venue mini-card — links to the shared directory */}
      <VenueCard ev={ev} venue={linkedVenue} />

      <WeatherWidget eventId={ev.id} variant="forecast" canEdit={canEdit} />
    </div>
  );
}

function VenueCard({ ev, venue }) {
  // Not linked to directory — show a compact prompt.
  if (!venue) {
    return (
      <section className="bevel border-hair rounded-sm p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-1">MOD.VENUE</div>
            <div className="font-display text-lg font-bold text-app tracking-tight">
              {ev.venue || "No venue set"}
            </div>
            <p className="text-xs text-muted mt-1">
              {ev.venue
                ? "Free-text venue only. Link it to your directory to unlock contact, capacity, and floor plans."
                : "Pick a venue from your directory to bring in contact info, capacity, floor plans, and price range."}
            </p>
          </div>
          <Link to="/venues" data-testid="overview-open-venues" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-hair hover:border-[#0A0A0A] hover:bg-canvas rounded-sm shrink-0">
            <Building2 size={13} strokeWidth={1.5} /> Open directory <ArrowUpRight size={11} strokeWidth={1.75} />
          </Link>
        </div>
      </section>
    );
  }
  // Linked — show a compact profile
  const priceStr = formatPrice(venue);
  return (
    <section className="bg-surface border border-[#0A0A0A] rounded-sm p-6" data-testid="overview-venue-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-1 flex items-center gap-2">
            MOD.VENUE / LINKED · VEN.{venue.id.slice(-6).toUpperCase()}
          </div>
          <div className="font-display text-xl font-bold text-app tracking-tight">{venue.name}</div>
          {(venue.city || venue.region) && (
            <div className="text-xs text-muted mt-1">{[venue.city, venue.region].filter(Boolean).join(", ")}</div>
          )}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <MiniStat label="SEATED" value={venue.capacity_seated ? venue.capacity_seated.toLocaleString() : "—"} />
            <MiniStat label="STANDING" value={venue.capacity_standing ? venue.capacity_standing.toLocaleString() : "—"} />
            <MiniStat label="SIZE" value={venue.square_feet ? `${venue.square_feet.toLocaleString()} ft²` : "—"} />
            <MiniStat label="PRICE" value={priceStr || "—"} />
          </div>
          {(venue.phone || venue.website) && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              {venue.phone && <a href={`tel:${venue.phone}`} className="flex items-center gap-1 text-app hover:underline"><Phone size={11} strokeWidth={1.5} /> {venue.phone}</a>}
              {venue.website && <a href={venue.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-app hover:underline"><Globe size={11} strokeWidth={1.5} /> {new URL(venue.website).hostname}</a>}
            </div>
          )}
        </div>
        <Link to={`/venues/${venue.id}`} data-testid="overview-open-venue" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bevel-btn-primary text-white hover:bg-black rounded-sm shrink-0">
          <Building2 size={13} strokeWidth={1.75} /> Open venue <ArrowUpRight size={11} strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border-l border-hair pl-3">
      <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums text-app mt-0.5">{value}</div>
    </div>
  );
}

function formatPrice(v) {
  if (!v.price_min && !v.price_max) return "";
  const symbolMap = { USD: "$", EUR: "€", GBP: "£", CAD: "CA$", AUD: "A$", INR: "₹", MXN: "MX$", BRL: "R$", JPY: "¥" };
  const s = symbolMap[v.price_currency || "USD"] || `${v.price_currency || ""} `;
  const fmt = n => (typeof n === "number" ? n.toLocaleString() : "");
  if (v.price_min && v.price_max) return `${s}${fmt(v.price_min)}–${s}${fmt(v.price_max)}`;
  if (v.price_min) return `${s}${fmt(v.price_min)}+`;
  return `≤${s}${fmt(v.price_max)}`;
}

function Stat({ icon: Icon, label, primary, sub }) {
  return (
    <div className="bevel border-hair rounded-sm p-6 card-lift">
      <div className="flex items-center gap-2 eyebrow mb-4">
        <Icon size={14} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      <div className="font-serif text-4xl text-app leading-none">{primary}</div>
      <div className="text-xs text-muted font-light mt-2">{sub}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow mb-1.5">{label}</div>
      <div className="text-app font-light">{value}</div>
    </div>
  );
}
